import { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../../../../../i18n';
import {
  fetchDepartamentos,
  fetchOrganizaciones,
  type OrganizacionResumen,
} from '../../../../shared/services/api';
import { InfoTooltip } from '../../../../../components/ui/InfoTooltip';

interface DeptTipo {
  id: string;
  nombre: string;
  nombreCorto: string;
  orden: number;
  color: string;
}

interface DeptResponsable {
  id: string;
  nombre: string;
  apellidos: string;
}

interface DeptRaw {
  id: string;
  nombre: string;
  codigo: string;
  tipo: string;
  departamentoPadreId: string | null;
  organizacionId?: string;
  responsable: DeptResponsable | null;
  tipoUnidadOrganizativa: DeptTipo | null;
  _count?: { puestos: number };
}

interface DeptNode extends DeptRaw {
  children: DeptNode[];
}

function buildTree(depts: DeptRaw[]): DeptNode[] {
  const map = new Map<string, DeptNode>();
  for (const d of depts) {
    map.set(d.id, { ...d, children: [] });
  }
  const roots: DeptNode[] = [];
  for (const node of map.values()) {
    if (node.departamentoPadreId && map.has(node.departamentoPadreId)) {
      map.get(node.departamentoPadreId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

const styles = {
  container: {
    width: '100%',
    overflowX: 'auto' as const,
    padding: '24px 0',
  },
  treeWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    minWidth: 'fit-content',
  },
  childrenRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 0,
    position: 'relative' as const,
  },
  childWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    position: 'relative' as const,
  },
  node: {
    background: '#fff',
    border: '1px solid #E2E6ED',
    borderRadius: 10,
    padding: '12px 16px',
    minWidth: 160,
    textAlign: 'center' as const,
    position: 'relative' as const,
    cursor: 'default',
    transition: 'box-shadow 0.15s ease',
  },
  nodeHover: {
    boxShadow: '0 4px 16px rgba(31, 56, 100, 0.15)',
  },
  nodeName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1A1D23',
    margin: 0,
    lineHeight: 1.3,
    fontFamily: 'inherit',
  },
  tipoBadge: (bg: string): React.CSSProperties => ({
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 600,
    fontFamily: 'inherit',
    padding: '2px 8px',
    borderRadius: 4,
    marginTop: 6,
    background: bg,
    color: '#fff',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  }),
  puestosBadge: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 600,
    fontFamily: 'inherit',
    padding: '2px 8px',
    borderRadius: 10,
    marginTop: 4,
    marginLeft: 4,
    background: '#E5E7EB',
    color: '#6B7280',
  } as React.CSSProperties,
  responsable: {
    fontSize: 11,
    color: '#6B7280',
    fontStyle: 'italic' as const,
    marginTop: 4,
    fontFamily: 'inherit',
  },
  verticalLine: {
    width: 0,
    height: 28,
    borderLeft: '2px solid #D1D5DB',
    margin: '0 auto',
  },
  horizontalBar: {
    height: 0,
    borderTop: '2px solid #D1D5DB',
    position: 'absolute' as const,
    top: 0,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 40,
    color: '#6B7280',
    fontSize: 14,
    fontFamily: 'inherit',
  },
  empty: {
    textAlign: 'center' as const,
    padding: 40,
    color: '#9CA3AF',
    fontSize: 14,
    fontFamily: 'inherit',
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1F3864',
    marginBottom: 20,
    textAlign: 'center' as const,
    fontFamily: 'inherit',
  },
};

const MAX_DEPTH = 4;

interface TreeNodeProps {
  node: DeptNode;
  depth: number;
}

function TreeNode({ node, depth }: TreeNodeProps) {
  const [hovered, setHovered] = useState(false);
  const tipo = node.tipoUnidadOrganizativa;
  const puestosCount = node._count?.puestos ?? 0;
  const visibleChildren = depth < MAX_DEPTH ? node.children : [];

  return (
    <div style={styles.childWrapper}>
      <div
        style={{
          ...styles.node,
          ...(hovered ? styles.nodeHover : {}),
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <p style={styles.nodeName}>{node.nombre}</p>
        <div>
          {tipo && (
            <span style={styles.tipoBadge(tipo.color || '#6B7280')}>{tipo.nombreCorto}</span>
          )}
          {puestosCount > 0 && <span style={styles.puestosBadge}>{puestosCount}</span>}
        </div>
        {node.responsable && (
          <div style={styles.responsable}>
            {node.responsable.nombre} {node.responsable.apellidos}
          </div>
        )}
      </div>

      {visibleChildren.length > 0 && (
        <>
          <div style={styles.verticalLine} />
          <ChildrenRow nodes={visibleChildren} depth={depth + 1} />
        </>
      )}
    </div>
  );
}

interface ChildrenRowProps {
  nodes: DeptNode[];
  depth: number;
}

function ChildrenRow({ nodes, depth }: ChildrenRowProps) {
  if (nodes.length === 0) return null;

  return (
    <div style={{ position: 'relative' }}>
      {nodes.length > 1 && <HorizontalBar count={nodes.length} />}
      <div style={styles.childrenRow}>
        {nodes.map((child) => (
          <div
            key={child.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '0 16px',
            }}
          >
            <div style={styles.verticalLine} />
            <TreeNode node={child} depth={depth} />
          </div>
        ))}
      </div>
    </div>
  );
}

function HorizontalBar({ count }: { count: number }) {
  if (count <= 1) return null;
  const pct = ((count - 1) / count) * 100;
  const offset = (100 - pct) / 2;

  return (
    <div
      style={{
        ...styles.horizontalBar,
        left: `${offset}%`,
        width: `${pct}%`,
      }}
    />
  );
}

const selectStyle: React.CSSProperties = {
  padding: '6px 12px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 12,
  fontFamily: 'inherit',
  background: '#fff',
};

export function DepartamentosArbol() {
  const { t } = useI18n();
  const [depts, setDepts] = useState<DeptRaw[]>([]);
  const [orgs, setOrgs] = useState<OrganizacionResumen[]>([]);
  const [orgFiltro, setOrgFiltro] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchDepartamentos({ porPagina: '100' }), fetchOrganizaciones()])
      .then(([deptRes, orgRes]) => {
        if (!cancelled) {
          setDepts((deptRes.data ?? []) as unknown as DeptRaw[]);
          setOrgs(orgRes.data ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDepts([]);
          setOrgs([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(
    () => (orgFiltro ? depts.filter((d) => d.organizacionId === orgFiltro) : depts),
    [depts, orgFiltro],
  );
  const tree = useMemo(() => buildTree(filtered), [filtered]);

  if (loading) {
    return <div style={styles.loading}>{t.structure.orgs.loadingOrgs}</div>;
  }

  if (depts.length === 0) {
    return <div style={styles.empty}>{t.structure.orgs.noDepts}</div>;
  }

  return (
    <div style={styles.container}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div
          style={{ ...styles.title, marginBottom: 0, display: 'inline-flex', alignItems: 'center' }}
        >
          {t.structure.admin.departamentos}
          <InfoTooltip
            title={t.structure.tooltips.departamentosArbol.titulo}
            text={t.structure.tooltips.departamentosArbol.ayuda}
          />
        </div>
        <select
          value={orgFiltro}
          onChange={(e) => setOrgFiltro(e.target.value)}
          style={selectStyle}
        >
          <option value="">{t.sidebar.allOrgs ?? 'Todas las organizaciones'}</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.nombre}
            </option>
          ))}
        </select>
      </div>
      <div style={styles.treeWrapper}>
        {tree.length === 0 ? (
          <div style={styles.empty}>{t.structure.orgs.noDepts}</div>
        ) : (
          tree.map((root) => <TreeNode key={root.id} node={root} depth={0} />)
        )}
      </div>
    </div>
  );
}
